import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, ChevronRight, AlertCircle, Lock } from "lucide-react";
import { MobileAppHeader } from "@/components/mobile/MobileAppHeader";
import {
  MEmpty,
  MPill,
  MSegmented,
} from "@/components/mobile/ui/primitives";
import {
  useApprovalGateMode,
  useApprovalsInbox,
  useApprovalsOutbox,
} from "@/hooks/useApprovals";
import {
  humanizeAction,
  tierLabel,
  type ApprovalRequest,
  type ApprovalRiskTier,
  type ApprovalStatus,
} from "@/services/approvalsApi";
import { cn } from "@/lib/utils";

type Tab = "inbox" | "outbox";
type Filter = "all" | "two-person" | "expiring";

const tierAccent: Record<ApprovalRiskTier, string> = {
  AUTO: "bg-[color:var(--m-gray-3)]",
  NOTIFY: "bg-[color:var(--m-info)]",
  APPROVE: "bg-[color:var(--m-warn)]",
  TWO_PERSON: "bg-[color:var(--m-crit)]",
  DENY: "bg-[color:var(--m-ink)]",
};

const statusTone: Record<ApprovalStatus, string> = {
  PENDING: "text-[color:var(--m-warn)] bg-[color:var(--m-warn-bg)]",
  APPROVED: "text-[color:var(--m-ok)] bg-[color:var(--m-ok-bg)]",
  EXECUTED: "text-[color:var(--m-ok)] bg-[color:var(--m-ok-bg)]",
  DENIED: "text-[color:var(--m-crit)] bg-[color:var(--m-crit-bg)]",
  EXPIRED: "text-[color:var(--m-gray)] bg-[color:var(--m-fill-2)]",
  BROKEN_GLASS: "text-[color:var(--m-indigo)] bg-[color:var(--m-indigo-bg)]",
};

function timeAgo(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "—";
  const diff = Math.max(0, Date.now() - d);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

function expiresIn(iso?: string): { label: string; soon: boolean } | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = t - Date.now();
  if (diff <= 0) return { label: "expired", soon: true };
  const h = Math.floor(diff / 3600000);
  if (h < 1) return { label: `<1h`, soon: true };
  if (h < 24) return { label: `${h}h`, soon: h <= 4 };
  return null;
}

function ApprovalCard({
  request,
  onOpen,
}: {
  request: ApprovalRequest;
  onOpen: () => void;
}) {
  const tier = request.riskTier;
  const exp = expiresIn(request.expiresAt);
  const required = request.quorumRequired ?? (tier === "TWO_PERSON" ? 2 : 1);
  const have = request.quorumCount ?? 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="m-card relative w-full overflow-hidden p-3.5 pl-4 text-left tap-feedback"
    >
      <span
        aria-hidden
        className={cn("absolute inset-y-0 left-0 w-1", tierAccent[tier])}
      />
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded-md bg-[color:var(--m-fill-2)] px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-[color:var(--m-main,#333)]">
          {tierLabel(tier)}
        </span>
        <span className="text-[11px] font-medium text-[color:var(--m-gray-3)]">
          {timeAgo(request.createdAt)}
        </span>
        {exp && (
          <span
            className={cn(
              "ml-auto text-[11px] font-semibold",
              exp.soon
                ? "text-[color:var(--m-crit)]"
                : "text-[color:var(--m-gray)]",
            )}
          >
            {exp.label === "expired" ? "Expired" : `Expires in ${exp.label}`}
          </span>
        )}
      </div>
      <h4 className="mb-0.5 text-[14px] font-semibold text-[color:var(--m-ink)]">
        {humanizeAction(request.actionType)}
      </h4>
      <p className="m-0 text-[11.5px] text-[color:var(--m-gray)]">
        Requested by{" "}
        <span className="m-mono text-[11px] text-[color:var(--m-ink)]">
          {request.requesterEmail || request.requesterId || "—"}
        </span>
      </p>
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[11px] font-semibold text-[color:var(--m-ink)]">
          {have} of {required}
        </span>
        <div className="flex gap-1">
          {Array.from({ length: required }).map((_, i) => (
            <span
              key={i}
              className={cn(
                "h-1.5 w-5 rounded-sm",
                i < have
                  ? "bg-[color:var(--m-ink)]"
                  : "bg-[color:var(--m-line-2)]",
              )}
            />
          ))}
        </div>
        <ChevronRight className="ml-auto h-4 w-4 text-[color:var(--m-gray-3)]" aria-hidden />
      </div>
    </button>
  );
}

function OutboxCard({ request, onOpen }: { request: ApprovalRequest; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="m-card flex w-full items-center gap-3 p-3 text-left tap-feedback"
    >
      <span
        aria-hidden
        className={cn("h-8 w-1 rounded-sm", tierAccent[request.riskTier])}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-[color:var(--m-ink)]">
          {humanizeAction(request.actionType)}
        </p>
        <p className="truncate text-[11px] text-[color:var(--m-gray-3)]">
          {tierLabel(request.riskTier)} · {timeAgo(request.createdAt)}
        </p>
      </div>
      <span
        className={cn(
          "rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
          statusTone[request.status],
        )}
      >
        {request.status.replace("_", " ")}
      </span>
    </button>
  );
}

export default function MobileApprovalsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("inbox");
  const [filter, setFilter] = useState<Filter>("all");
  const inbox = useApprovalsInbox();
  const outbox = useApprovalsOutbox();
  const gateMode = useApprovalGateMode();

  const inboxItems = useMemo(() => {
    const data = (inbox.data ?? []).filter((r) => r.status === "PENDING");
    if (filter === "two-person")
      return data.filter((r) => r.riskTier === "TWO_PERSON");
    if (filter === "expiring") {
      const cutoff = Date.now() + 4 * 60 * 60 * 1000;
      return data.filter(
        (r) => r.expiresAt && new Date(r.expiresAt).getTime() < cutoff,
      );
    }
    return data;
  }, [inbox.data, filter]);

  const outboxItems = outbox.data ?? [];
  const inboxCount = (inbox.data ?? []).filter((r) => r.status === "PENDING").length;

  return (
    <div className="pb-6">
      <MobileAppHeader />
      <div className="px-4">
        <div className="mb-3 flex items-center gap-2">
          <h1 className="m-h2">Approvals</h1>
          {gateMode.data && (
            <span
              className={cn(
                "ml-auto inline-flex items-center gap-1 rounded-md border border-[color:var(--m-line)] bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[color:var(--m-gray)]",
              )}
              title={`Gate mode: ${gateMode.data.mode}`}
            >
              {gateMode.data.complianceLocked && (
                <Lock className="h-3 w-3 text-[color:var(--m-warn)]" aria-hidden />
              )}
              {gateMode.data.mode}
            </span>
          )}
        </div>

        <MSegmented
          value={tab}
          onChange={(v) => setTab(v as Tab)}
          options={[
            { value: "inbox", label: `Inbox${inboxCount ? ` (${inboxCount})` : ""}` },
            { value: "outbox", label: "Outbox" },
          ]}
          className="mb-3"
        />

        {tab === "inbox" && (
          <>
            <div className="mb-3 flex gap-2 overflow-x-auto pb-1" data-mobile-hscroll>
              <MPill active={filter === "all"} onClick={() => setFilter("all")}>
                All
              </MPill>
              <MPill
                active={filter === "two-person"}
                onClick={() => setFilter("two-person")}
              >
                Two-person
              </MPill>
              <MPill
                active={filter === "expiring"}
                onClick={() => setFilter("expiring")}
              >
                Expiring soon
              </MPill>
            </div>

            {inbox.isError ? (
              <MEmpty
                title="Couldn't load approvals"
                description={
                  (inbox.error as Error)?.message ??
                  "Try again in a moment."
                }
                icon={<AlertCircle className="h-4 w-4" aria-hidden />}
              />
            ) : inboxItems.length === 0 ? (
              <MEmpty
                title="Inbox zero"
                description="No approval requests waiting on you right now."
                icon={<Inbox className="h-4 w-4" aria-hidden />}
              />
            ) : (
              <div className="flex flex-col gap-2.5">
                {inboxItems.map((r) => (
                  <ApprovalCard
                    key={r.requestId}
                    request={r}
                    onOpen={() => navigate(`/m/approvals/${r.requestId}`)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {tab === "outbox" && (
          <>
            {outbox.isError ? (
              <MEmpty
                title="Couldn't load outbox"
                description={(outbox.error as Error)?.message}
                icon={<AlertCircle className="h-4 w-4" aria-hidden />}
              />
            ) : outboxItems.length === 0 ? (
              <MEmpty
                title="Nothing submitted"
                description="Gated actions you trigger will appear here."
                icon={<Inbox className="h-4 w-4" aria-hidden />}
              />
            ) : (
              <div className="flex flex-col gap-2">
                {outboxItems.map((r) => (
                  <OutboxCard
                    key={r.requestId}
                    request={r}
                    onOpen={() => navigate(`/m/approvals/${r.requestId}`)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
